import {
  HiExclamationCircle,
  HiOutlineDocumentDuplicate,
  HiOutlineUpload,
  HiOutlineX,
} from "react-icons/hi";
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import ROLE from "../../modules/role";
import { ClipLoader } from "react-spinners";
import ApiUser from "../../modules/api/user";
import { Alert, Modal } from "react-bootstrap";
import Notifier from "../../modules/notifier/notifier";
import Debouncer from "../../modules/debouncer/debouncer";
import ModulePopup from "../../modules/popup/popup";
import MM_SAMPLE_IMPORT_CSV_RESULTS, { TImportResponseStatus } from "../../data/manage-member-import-csv-results";
import BasicDataTable from "../common/BasicDataTable";

const SHOW_EXAMPLE_IMPORT_RESULT = false;

export type TPopupImportMembersRef = {
  show: () => void;
};

export type TPopupImportMembersProps = {
  role: string;
  groupId: string;
  title?: string;
  labelOK?: string;
  sampleFile?: string;
  sampleFileName?: string;
  backdrop?: boolean|"static";
  keyboard?: boolean;
  onClose?: () => void;
  onOK?: () => void;
  onImport?: () => void;
  entity?: string;
};

const PopupImportMembers = forwardRef<
  TPopupImportMembersRef,
  TPopupImportMembersProps
>((props, ref) => {
  const fileRef = useRef<HTMLInputElement>();
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);
  const [sending, setSending] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [results, setResults] = useState<TImportResponseStatus[]>([]);
  const [showDropArea, setShowDropArea] = useState(false);

  useEffect(() => {
    if (!results.length) {
      return;
    }
    if (results.some((t) => t.success)) {
      props.onImport();
    }
  }, [results]);

  useEffect(() => {
    if (!SHOW_EXAMPLE_IMPORT_RESULT) {
      setResults([]);
    }
  }, [uploadedFile]);

  useImperativeHandle(ref, () => ({
    show() {
      if (SHOW_EXAMPLE_IMPORT_RESULT) {
        setResults(MM_SAMPLE_IMPORT_CSV_RESULTS);
      }

      setShow(true);
      setShowDropArea(true);
    },
  }));

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const onFileAttached = (file: File) => {
    if (validateFileType(file)) {
      setShowDropArea(false);
      setError("");
      setUploadedFile(file);
    } else {
      resetFileValue();
      setError("Invalid file type. Please upload a CSV file.");
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    onFileAttached(file);
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    onFileAttached(file);
  };

  const handleUploadAreaClick = () => {
    resetFileValue(false);
    document.getElementById("member-form-upload-file-input")?.click();
  };

  const handleRemoveFile = () => {
    resetFileValue();
    setUploadedFile(null);
    setError("");
    setShowDropArea(true);
  };

  const handleSubmit = async (validationNeeded?: boolean) => {
    if (validationNeeded) {
      const { errors, warnings } = await ApiUser.validateCsv(uploadedFile);
      if (errors.length) {
        return;
      }
      if (warnings.length) {
        ModulePopup.showAlertConfirm({
          title: <>
            <HiExclamationCircle />
            Alert
          </>,
          description: <>
            {warnings.map((v, i) => <React.Fragment key={i}>
              {v}
            </React.Fragment>)}
          </>,
          onYes() {
            handleSubmit();
          }
        });
      } else {
        handleSubmit();
      }
      return;
    }
    Debouncer.execute("IMPORT_MEMBERS", async () => {
      setSending(true);
      setResults([]);
      await ApiUser.importByCsvNew(uploadedFile, props.role, props.groupId)
        .then(({ success, results, message }) =>
          success ? setResults(results) : setError(message)
        )
        .catch((error) => {
          Notifier.error(error.message);
        });
      setSending(false);
    });
  };

  const validateFileType = (file: File) => {
    const allowedTypes = ["text/csv"];
    return allowedTypes.includes(file.type);
  };

  const resetFileValue = (resetUploadedFile = true) => {
    try {
      fileRef.current.value = "";
      if (resetUploadedFile) {
        setUploadedFile(null);
      }
    } catch (ignore) {}
  };

  const onHide = () => {
    setShow(false);
    handleRemoveFile();
    props.onClose();
  };

  return (
    <Modal
      className="modal-generic modal-import"
      show={show}
      backdrop={props.backdrop}
      keyboard={props.keyboard}
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title>{props.title ?? "Confirmation"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          <a href={props.sampleFile} target="_blank">
            Click here to download the template (
            <strong>{props.sampleFileName}</strong>) and fill in the particulars
          </a>
          .
        </p>
        <div
          className={`import-upload-area border rounded col-12 text-center
            ${ isDragging ? "dragging" : "" }
            ${ showDropArea && results.length === 0 ? "" : "d-none" }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadAreaClick}
        >
          <HiOutlineUpload className="upload-icon mb-3 text-secondary" />
          <p className="mb-1 fs-5">Drag & Drop or Choose the CSV file to upload</p>
          <p className="text-secondary fs-5 d-none">CSV file</p>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            id="member-form-upload-file-input"
            className="d-none"
          />
        </div>
        {uploadedFile && (
          <div className="uploaded-file-container mt-3 p-2 rounded">
            <div className="uploaded-file d-flex justify-content-between align-items-center p-2 bg-white rounded">
              <p className="mb-0">
                <span>
                  <HiOutlineDocumentDuplicate className="fs-4 me-2" color="#2EC4B6" />
                </span>
                {uploadedFile.name}
              </p>
              <HiOutlineX className="cancel-icon" onClick={handleRemoveFile} title="Delete" />
            </div>
          </div>
        )}
        {error && error !== "" && (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {error}
          </div>
        )}
        <ImportResultRenderer entity={props.entity} results={results} setResults={setResults} />
      </Modal.Body>
      <Modal.Footer>
        {(results.length > 0 || error) && <button
          className="btn btn-mw btn-secondary"
          onClick={() => {
            ModulePopup.showAlertConfirm({
              title: "Re-import Confirmation",
              description: "Are you sure to re-import?",
              onYes() {
                setResults([]);
                handleRemoveFile();
              }
            });
          }}
          disabled={sending}
        >
          Re-import
        </button>}
        {" "}
        {results.length === 0 && !error && <button
          className="btn btn-mw btn-secondary"
          onClick={() => handleSubmit(props.role === ROLE.STUDENT)}
          disabled={!uploadedFile || sending}
        >
          <ClipLoader color="#FFFFFF" size={16} loading={sending} />
          {!sending && (props.labelOK ?? "Import")}
        </button>}
        {" "}
        <button
          className="btn btn-mw btn-dark"
          onClick={() => {
            onHide();
            return;
            if (uploadedFile || error || results.length > 0) {
              ModulePopup.showAlertConfirm({
                title: "Close Confirmation",
                description: "Are you sure to close?",
                onYes() {
                  onHide();
                }
              });
            } else {
              onHide();
            }
          }}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
});

PopupImportMembers.defaultProps = {
  entity: "",
  title: "Import Member(s)",
  backdrop: "static",
  keyboard: false,
  onClose: () => {},
  onOK: () => {},
  onImport: () => {},
};

function ImportResultRenderer({
  results,
  setResults,
  entity
}: {
  entity: string;
  results: TImportResponseStatus[];
  setResults: React.Dispatch<React.SetStateAction<TImportResponseStatus[]>>;
}) {
  const MemoizedResults = useMemo(() => {
    const total = results.length;

    if (total === 0) {
      return <></>;
    }

    const successCount = results.filter(i => i.success).length;
    const errorCount = results.filter(i => !i.success).length;
    const newCount = results.filter(i => i?.action === "new").length;
    const updateCount = results.filter(i => i?.action === "update").length;
    const errorItems = results.map((item, index) => {
      // the CSV row index started from 1, since 1 occupied by heading column,
      // we use 2. so that's why it's added by 2.
      //
      // also, we re-check the new props, just in case not yet available, FE
      // won't show runtime error.
      return {
        ...item,
        column: typeof item.column === "string" ? item.column : "N/A",
        action: typeof item.action === "string" ? item.action : "error",
        messages: Array.isArray(item.messages) ? item.messages : [],
        rowIndex: item.row + 2,
      };
    }).filter(i => !i.success);

    return <>
      <Alert
        variant={errorCount ? "danger" : successCount === total ? "info" : "info"}
        onClose={() => setResults([])}
        className="modal-import-status import-csv-email-notify-status"
        /**
         * As requested by client, no need to have dismissable feature, since
         * "Re-import" button (requested as well) is introduced.
         */
        dismissible={false}
      >
        {errorCount > 0 && <>
          <p style={{ fontSize: "20px" }}>Errors found in the CSV! Please correct the entries shown below and re-import.</p>
        </>}

        {successCount === total && <>
          <h4 style={{ color: "rgb(5 49 58)" }}>Successfully imported {successCount} {entity}(s)!</h4>
          <p style={{ color: "rgb(5 49 58)" }}>An email has been sent to the {entity}(s) to login to GROW-Pro.</p>
        </>}

        {/* Client only wants to show the error detail. Success no need to show.  */}
        {errorCount > 0 && <div className="import-csv-result-datatable">
          <BasicDataTable
            pagination={false}
            data={errorItems}
            customStyles={{
              table: {
                style: {
                  maxHeight: "calc(100vh - 370px)",
                  minHeight: "200px",
                  overflowY: "auto"
                }
              }
            }}
            columns={[
              {
                id: "row",
                name: "Row",
                cell: (row) => row?.rowIndex ?? "Error",
                maxWidth: "100px"
              },
              {
                id: "column",
                name: "Column",
                cell: (row) => row?.column ?? "N/A"
              },
              {
                id: "messages",
                name: "Correction Required",
                cell: (row) => <>
                  {row.messages.length === 0 && "N/A"}
                  {row.messages.length > 0 && <div>
                    {row.messages.map((item, index) => <div key={index} style={{marginBottom:"4px", marginTop:"4px"}}>
                      {item}
                    </div>)}
                  </div>}
                </>
              }
            ]}
          />
        </div>}

        <p className="d-none">
          New: {newCount} / {total} <br/>
          Updated: {updateCount} / {total} <br/>
          Error: {errorCount} / {total}
        </p>
      </Alert>
    </>
  }, [results]);

  return <>{MemoizedResults}</>;
}

export default PopupImportMembers;
